import * as React from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useAuthStore } from "@/store/authStore";
import { Handoff } from "@/components/auth/Handoff";

/**
 * Route wrapper for every authenticated screen. Blocks rendering until the
 * auth store finishes its bootstrap check, then either renders children
 * (via <Outlet/>) or redirects to /login with the original path captured in
 * ?next=... so we land back on it post-login.
 */
// DEV-ONLY: set localStorage.__dev_bypass=1 to skip Cognito for UI review.
// Remove before shipping.
const DEV_BYPASS =
  import.meta.env.DEV &&
  typeof localStorage !== "undefined" &&
  localStorage.getItem("__dev_bypass") === "1";

export function AuthGate(): React.ReactElement {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (DEV_BYPASS) return <Outlet />;

  if (status === "loading") {
    // MV2-910: the session bootstrap wears the same hand-off frame as the
    // /me fetch on `/` — one moment, not two different loaders.
    return <Handoff state="loading" />;
  }
  if (status === "unauthenticated") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}
